import {
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CDataTable,
  CPagination,
  CRow,
  CLabel,
  CInputGroup,
  CInputGroupText,
  CInput,
  CBadge,
  CTooltip,
  CButtonGroup,
  CSelect,
} from "@coreui/react";
import { useEffect, useState } from "react";
import { BsFillPlusCircleFill } from "react-icons/bs";
import { useHistory } from "react-router-dom";
import { getList } from "../../api/generics";
import { getRoutePath } from "../../routes";
import { SUCCESS } from "../../utils/constants/tags";
import { successAlert, warningAlert } from "../utils/messages";
import PaymentModal from "./PaymentModal";
import {
  ClientPayment,
  getPaymentStatusClass,
  getPaymentTypeLabel,
  newClientPayment,
  getPaymentStatusLabel,
  PAYMENTSTATUSES,
} from "../../models/client";
import { formatToCurrency } from "../../currency/format";
import { PYG } from "../../currency/available-currencies";
import { DATEDDMMYYY, formatDate, formatDateTime } from "../../utils/dates";
import Datetime from "react-datetime";
import moment from "moment";
import { PAYMENTS_CREATE } from "../../auth/permissions";
import PermissionRequiredComponent from "../permissions/PermissionRequiredComponent";
import {
  defaultValueOnUndefined,
  emptyValueOnUndefined,
} from "../../utils/fields";
import PaymentRevertModal from "./PaymentRevertModal";
import RelatedOrders from "./RelatedOrders";
import generatePaymentReportPDF from "../../utils/paymentsPdfGenerator";
import { Spinner } from "react-bootstrap";

const ITEMS_PER_PAGE = 20;
const ITEMS_PER_PAGE_FOR_REPORT = -1; // Basically No limit

const PaymentList = () => {
  const fields = [
    {
      key: "date",
      _classes: ["text-center", "align-middle"],
      label: "Fecha",
    },
    {
      key: "clientName",
      _classes: ["text-center", "align-middle"],
      label: "Cliente",
    },
    {
      key: "amountPaid",
      _classes: ["text-center", "align-middle"],
      label: "Montos Gs",
    },
    {
      key: "status",
      _classes: ["text-center", "align-middle"],
      label: "Estado",
    },
    {
      key: "paymentType",
      _classes: ["text-center", "align-middle"],
      label: "Medio de pago",
    },
    {
      key: "bankName",
      _classes: ["text-center", "align-middle"],
      label: "Cheque",
    },
    {
      key: "relatedOrder",
      _classes: ["text-center", "align-middle"],
      label: "Orden de envío",
      filter: false,
    },
    {
      key: "obs",
      _classes: ["text-center", "align-middle"],
      label: "Observación",
      filter: false,
    },
    {
      key: "actions",
      _classes: ["text-center"],
      label: "Acciones",
      filter: false,
    },
  ];

  const [showPaymentRevertModal, setShowPaymentRevertModal] = useState(false);

  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const [payment, setPayment] = useState<ClientPayment | undefined>(undefined);
  const [search, setSearch] = useState<string>("");
  const [startDate, setStartDate] = useState<string | moment.Moment>("");
  const [endDate, setEndDate] = useState<string | moment.Moment>("");
  const [filterType, setFilterType] = useState<string>("");
  const [enableReportButton, setEnableReportButton] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState<ClientPayment[]>([]);

  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const history = useHistory();

  const getURLParams = () => {
    let urlParams = new URLSearchParams(history.location.search);
    let page = urlParams.get("page")
      ? parseInt(urlParams.get("page") as string)
      : 1;

    if (page <= 0) {
      page = 1;
    }
    let search = urlParams.get("search") ? urlParams.get("search") : "";
    let startDate = urlParams.get("start_date")
      ? urlParams.get("start_date")
      : "";
    let endDate = urlParams.get("end_date") ? urlParams.get("end_date") : "";
    let paymentStatus = urlParams.get("payment_status")
      ? urlParams.get("payment_status")
      : "";
    let map = new Map();
    map.set("search", search);
    map.set("page", page);
    if (paymentStatus !== "") map.set("payment_status", paymentStatus);
    if (startDate !== "") map.set("start_date", startDate);
    if (endDate !== "") map.set("end_date", endDate);
    return map;
  };

  const fetchPayments = async () => {
    const urlParams = getURLParams();
    const limit = ITEMS_PER_PAGE;
    const offset = ITEMS_PER_PAGE * Number(urlParams.get("page") - 1);
    const paymentsStatus = await getList<ClientPayment>(
      "/payments/",
      limit,
      offset,
      urlParams
    );
    if (paymentsStatus.status === SUCCESS) {
      if (paymentsStatus.data !== undefined) {
        const count = paymentsStatus.data.count;
        const pages = Math.ceil((count ? count : 0) / ITEMS_PER_PAGE);
        setTotalPages(pages);
        setPayments(paymentsStatus.data.items);
        setCurrentPage(urlParams.get("page"));
      }
    } else {
      const message = paymentsStatus.detail
        ? paymentsStatus.detail
        : "Error desconocido";
      warningAlert(message);
    }
    setLoading(false);
  };

  const fetchPaymentsReport = async () => {
    setEnableReportButton(false);
    const urlParams = getURLParams();
    urlParams.set("descending", "false");
    const limit = ITEMS_PER_PAGE_FOR_REPORT;
    const offset = 0;
    const paymentsStatus = await getList<ClientPayment>(
      "/payments/",
      limit,
      offset,
      urlParams
    );
    if (paymentsStatus.status === SUCCESS) {
      if (paymentsStatus.data !== undefined) {
        return paymentsStatus.data.items;
      }
      return [];
    } else {
      return [];
    }
  };

  const onPageChange = (page: number) => {
    setLoading(true);
    let urlParams = new URLSearchParams(history.location.search);
    let previousParams = getURLParams();
    urlParams.set(
      "search",
      previousParams.get("search") ? previousParams.get("search") : ""
    );
    if (previousParams.get("start_date")) {
      urlParams.set("start_date", previousParams.get("start_date"));
    }
    if (previousParams.get("end_date")) {
      urlParams.set("end_date", previousParams.get("end_date"));
    }
    if (previousParams.get("payment_status")) {
      urlParams.set("payment_status", previousParams.get("order_status"));
    }
    urlParams.set("page", page.toString());
    let url = `?${urlParams.toString()}`;
    history.push(url);
  };

  const onStartDatehange = (value: string | moment.Moment) => {
    const newDate = value as moment.Moment;
    if (newDate === undefined || !moment(value, true).isValid()) {
      setStartDate("");
      return;
    }
    setStartDate(value);
  };

  const onEndDatehange = (value: string | moment.Moment) => {
    const newDate = value as moment.Moment;
    if (newDate === undefined || !moment(value, true).isValid()) {
      setEndDate("");
      return;
    }
    setEndDate(value);
  };

  const onFilterTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilterType(e.target.value);
  };

  const onAddClick = () => {
    setShowPaymentModal(true);
  };

  const onSizeEditCancel = () => {
    setPayment(undefined);
    setShowPaymentModal(false);
  };

  const onSizeEditSuccess = () => {
    successAlert("Datos guardados con éxito!");
    setShowPaymentModal(false);
    setPayments([]);
    setLoading(true);
    fetchPayments();
  };

  const onRevertClick = (payment: ClientPayment) => {
    setPayment(payment);
    setShowPaymentRevertModal(true);
  };

  const onPaymentRevertCancel = () => {
    setPayment(undefined);
    setShowPaymentRevertModal(false);
  };

  const onPaymentRevertSuccess = () => {
    successAlert("Registro revertido con éxito!");
    setShowPaymentRevertModal(false);
    setPayments([]);
    setLoading(true);
    fetchPayments();
  };

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const onSearch = () => {
    setLoading(true);
    let urlParams = new URLSearchParams(history.location.search);
    if (search !== "") {
      urlParams.set("search", search);
    } else {
      urlParams.delete("search");
    }
    if (moment.isMoment(startDate)) {
      urlParams.set("start_date", startDate.toISOString());
    } else if (startDate !== undefined && startDate !== "") {
      urlParams.set("start_date", startDate);
    } else {
      urlParams.delete("start_date");
    }
    if (moment.isMoment(endDate)) {
      urlParams.set("end_date", endDate.toISOString());
    } else if (endDate !== undefined && endDate !== "") {
      urlParams.set("end_date", endDate);
    } else {
      urlParams.delete("end_date");
    }
    if (filterType !== "") {
      urlParams.set("payment_status", filterType);
    } else {
      urlParams.delete("payment_status");
    }
    let url = `?${urlParams.toString()}`;
    history.push(url);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      onSearch();
    }
  };

  const evaluateReport = async () => {
    setGeneratingReport(false);
    const urlParams = getURLParams();

    const startDateStr = urlParams.get("start_date");
    const endDateStr = urlParams.get("end_date") || new Date().toISOString();

    if (!startDateStr) {
      setEnableReportButton(false);
      return;
    }

    const startDate = new Date(startDateStr);
    const endDate = new Date(endDateStr);

    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      setEnableReportButton(false);
      return;
    }

    const diffInDays = Math.floor(
      (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
    );
    if (diffInDays > 31) {
      setEnableReportButton(false);
      return;
    }
    setEnableReportButton(true);
  };

  const onReportClick = async () => {
    setEnableReportButton(false);
    setGeneratingReport(true);
    const urlParams = getURLParams();

    const startDateStr = urlParams.get("start_date");
    const endDateStr = urlParams.get("end_date") || new Date().toISOString();

    if (!startDateStr) {
      warningAlert(
        "El rango de fechas para reporte no debe ser mayor a un mes (31 días)."
      );
      setEnableReportButton(true);
      setGeneratingReport(false);
      return;
    }

    const startDate = new Date(startDateStr);
    const endDate = new Date(endDateStr);

    // Verificar que las fechas sean válidas
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      warningAlert("Los parámetros de fechas son inválidos.");
      setEnableReportButton(true);
      setGeneratingReport(false);
      return;
    }

    // Verificar rango de fechas (máximo 31 días)
    const diffInDays = Math.floor(
      (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
    );
    if (diffInDays > 31) {
      warningAlert(
        "El rango de fechas para reporte no debe ser mayor a un mes (31 días)."
      );
      setEnableReportButton(true);
      setGeneratingReport(false);
      return;
    }

    const reportPayments = await fetchPaymentsReport();
    if (reportPayments.length <= 0) {
      warningAlert("No se han seleccionado registros");
      setEnableReportButton(true);
      setGeneratingReport(false);
      return;
    }

    const titlesRight: string[] = generateTitlesRight(urlParams);

    const filteredTitlesRight = titlesRight.filter((title) => title !== "");

    await generatePaymentReportPDF(
      reportPayments,
      [
        "Reporte de Pagos",
        "",
        "Reporte generado el:",
        formatDateTime(new Date()),
      ],
      filteredTitlesRight
    );
    setEnableReportButton(true);
    setGeneratingReport(false);
  };

  const generateTitlesRight = (map: Map<any, any>): string[] => {
    const titlesRight: string[] = ["Filtros Utilizados: "];

    map.forEach((value, key) => {
      const stringKey = typeof key === "string" ? key : "";
      const stringValue = typeof value === "string" ? value : "";
      const description = getFilterDescription(stringKey, stringValue);
      titlesRight.push(description);
    });

    return titlesRight;
  };

  const getFilterDescription = (key: string, value: string) => {
    switch (key) {
      case "search":
        if (value !== "") {
          return `Búsqueda: ${value}`;
        } else {
          return "";
        }
      case "start_date":
        return `Fecha de inicio: ${formatDateTime(new Date(value))}`;
      case "end_date":
        return `Fecha de fin: ${formatDateTime(new Date(value))}`;
      default:
        return "";
    }
  };

  useEffect(() => {
    fetchPayments();
    evaluateReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return history.listen((location) => {
      if (location.pathname !== getRoutePath("payments")) {
        return;
      }
      fetchPayments();
      evaluateReport();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  return (
    <>
      <CRow>
        <CCol lg="12">
          <CCard>
            <CCardHeader className="d-flex flex-row mb-3">
              <div className="p-2 ">
                <h3>Pagos</h3>
              </div>
              <div className="p-2 ">
                <PermissionRequiredComponent permissionName={PAYMENTS_CREATE}>
                  <CButton
                    size="primary"
                    className="float-right"
                    onClick={onAddClick}
                  >
                    <BsFillPlusCircleFill />
                    &nbsp; Agregar Nuevo
                  </CButton>
                </PermissionRequiredComponent>
              </div>
            </CCardHeader>
            <CCardBody>
              <CRow>
                <CCol>
                  {generatingReport ? (
                    <CButton
                      color="danger"
                      className="fa-solid fa-file-pdf"
                      onClick={onReportClick}
                      disabled={!enableReportButton}
                    >
                      <Spinner
                        animation="grow"
                        style={{
                          height: "17px",
                          width: "17px",
                          marginTop: "auto",
                          marginBottom: "auto",
                          marginRight: "10px",
                        }}
                      />
                      Generando...
                    </CButton>
                  ) : (
                    <CTooltip
                      content={
                        !enableReportButton || loading
                          ? "Debe seleccionar un rango máximo de 31 días"
                          : "La generación de un reporte podria demorar unos minutos. Tenga paciencia"
                      }
                    >
                      <CButton
                        color="danger"
                        className="fa-solid fa-file-pdf"
                        onClick={onReportClick}
                        disabled={!enableReportButton || loading}
                      ></CButton>
                    </CTooltip>
                  )}
                  <p>
                    La generación de reporte esta limitada a un rango de 1 mes
                  </p>
                </CCol>
              </CRow>
              <CRow>
                <CCol>
                  <h5>Filtros</h5>
                </CCol>
              </CRow>
              <CRow>
                <CCol></CCol>
              </CRow>
              <CRow className={"mt-2"}>
                <CCol md={2}>
                  <CLabel className={"mt-2"}>Desde:</CLabel>
                </CCol>
                <CCol>
                  <Datetime
                    className="pl-0"
                    onChange={onStartDatehange}
                    value={startDate}
                    locale="es/PY"
                    dateFormat="DD/MM/YYYY"
                    timeFormat="HH:mm"
                    closeOnSelect={true}
                  ></Datetime>
                </CCol>
                <CCol md={2}>
                  <CLabel className={"mt-2"}>Hasta:</CLabel>
                </CCol>
                <CCol>
                  <Datetime
                    className="pl-0"
                    onChange={onEndDatehange}
                    value={endDate}
                    locale="es/PY"
                    dateFormat="DD/MM/YYYY"
                    timeFormat="HH:mm"
                    closeOnSelect={true}
                  ></Datetime>
                </CCol>
              </CRow>
              <CRow className={"mt-2"}>
                <CCol md={2}>
                  <CLabel className={"mt-2"}>Estado:</CLabel>
                </CCol>
                <CCol>
                  <CSelect
                    type="text"
                    defaultValue={filterType !== null ? filterType : ""}
                    onChange={onFilterTypeChange}
                  >
                    <option value={""}>-----</option>
                    {Object.entries(PAYMENTSTATUSES).map(([key, entry], ix) => {
                      return (
                        <option key={ix} value={key}>
                          {entry.name}
                        </option>
                      );
                    })}
                  </CSelect>
                </CCol>
              </CRow>
              <CRow>
                <CCol>
                  <CLabel
                    className="visually-hidden"
                    htmlFor="autoSizingInputGroup"
                  ></CLabel>
                  <CInputGroup>
                    <CInputGroupText>
                      <i className="fa fa-search"></i>
                    </CInputGroupText>
                    <CInput
                      type="text"
                      placeholder="Introduzca el termino de busqueda"
                      onChange={onSearchChange}
                      value={search}
                      onKeyDown={handleKeyDown}
                    />
                    <CButton
                      type="button"
                      size="primary"
                      variant="outline"
                      onClick={onSearch}
                    >
                      Buscar
                    </CButton>
                  </CInputGroup>
                </CCol>
              </CRow>
              <div>
                <CDataTable
                  noItemsView={<h2 className="text-center">Sin Resultados</h2>}
                  addTableClasses={"table-fixed"}
                  fields={fields}
                  items={payments}
                  striped
                  border
                  loading={loading}
                  responsive
                  scopedSlots={{
                    amountPaid: (item: ClientPayment) => {
                      const total =
                        (item.amountPaid ?? 0) + (item.discountInDebt ?? 0);
                      return (
                        <td className="text-center align-middle">
                          <div>
                            <small className="text-muted">Total</small>
                            <br />
                            <b>{formatToCurrency(total, PYG)}</b>
                          </div>
                          <div className="mt-1">
                            <small className="text-muted">Recibido</small>
                            <br />
                            <span className="text-success">
                              {formatToCurrency(item.amountPaid!, PYG)}
                            </span>
                          </div>
                          {item.discountInDebt ? (
                            <div className="mt-1">
                              <small className="text-muted">Descuento</small>
                              <br />
                              <span className="text-warning">
                                {formatToCurrency(item.discountInDebt, PYG)}
                              </span>
                            </div>
                          ) : null}
                        </td>
                      );
                    },
                    date: (item: ClientPayment) => {
                      return (
                        <td className="text-center align-middle">
                          <b>
                            {item.date
                              ? formatDate(new Date(item.date), DATEDDMMYYY)
                              : "-"}
                          </b>
                        </td>
                      );
                    },
                    clientName: (item: ClientPayment) => {
                      return (
                        <td className="text-center align-middle">
                          <b>{item.clientName}</b>
                          {item.clientRuc && (
                            <>
                              <br />
                              <small className="text-muted">{item.clientRuc}</small>
                            </>
                          )}
                        </td>
                      );
                    },
                    paymentType: (item: ClientPayment) => {
                      return (
                        <td className="text-center align-middle">
                          {getPaymentTypeLabel(item)}
                        </td>
                      );
                    },
                    bankName: (item: ClientPayment) => {
                      if (!item.bankName && !item.checkNumber && !item.checkDate) {
                        return <td className="text-center align-middle">-</td>;
                      }
                      return (
                        <td className="text-center align-middle">
                          {item.bankName && (
                            <div>
                              <small className="text-muted">Banco</small>
                              <br />
                              <b>{item.bankName}</b>
                            </div>
                          )}
                          {item.checkNumber && (
                            <div className="mt-1">
                              <small className="text-muted">Nro. Cheque</small>
                              <br />
                              <span>{item.checkNumber}</span>
                            </div>
                          )}
                          {item.checkDate && (
                            <div className="mt-1">
                              <small className="text-muted">Fecha</small>
                              <br />
                              <span>{formatDate(new Date(item.checkDate), DATEDDMMYYY)}</span>
                            </div>
                          )}
                        </td>
                      );
                    },
                    status: (item: ClientPayment) => {
                      return (
                        <td className="text-center align-middle">
                          <CBadge
                            color={
                              item.status
                                ? getPaymentStatusClass(item.status)
                                : undefined
                            }
                          >
                            {item.status
                              ? getPaymentStatusLabel(item.status)
                              : undefined}
                          </CBadge>
                        </td>
                      );
                    },
                    relatedOrder: (item: ClientPayment) => {
                      return (
                        <td className="text-center align-middle">
                          {item.deliveryOrderId ? (
                            `#${item.deliveryOrderId}`
                          ) : (
                            <RelatedOrders payment={item} />
                          )}
                        </td>
                      );
                    },
                    obs: (item: ClientPayment) => {
                      return (
                        <td className="text-center align-middle">
                          {item.obs ? item.obs : "-"}
                        </td>
                      );
                    },
                    actions: (item: ClientPayment) => {
                      const revertButton = (
                        <CTooltip content="Revertir/Anular">
                          <CButton
                            className="text-white"
                            color="warning"
                            onClick={() => {
                              onRevertClick(item);
                            }}
                          >
                            <i className="fa fa-clock-rotate-left"></i>
                          </CButton>
                        </CTooltip>
                      );

                      return (
                        <td className="text-right align-middle">
                          <CButtonGroup>
                            {item.status && item.status === "paid" ? (
                              revertButton
                            ) : (
                              <></>
                            )}
                          </CButtonGroup>
                        </td>
                      );
                    },
                  }}
                />
                <div className="d-flex justify-content-center">
                  <CPagination
                    pages={totalPages}
                    activePage={currentPage}
                    onActivePageChange={(i: number) => onPageChange(i)}
                    className={totalPages < 2 ? "d-none" : ""}
                  />
                </div>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      <PaymentModal
        show={showPaymentModal}
        onCancel={onSizeEditCancel}
        onSuccess={onSizeEditSuccess}
        payment={payment ? payment : newClientPayment()}
      />
      <PaymentRevertModal
        show={showPaymentRevertModal}
        onCancel={onPaymentRevertCancel}
        onSuccess={onPaymentRevertSuccess}
        payment={payment}
      />
    </>
  );
};

export default PaymentList;
